import React from 'react';
import { useState, useRef, useCallback, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { Board, Change } from '../../models';
import { Column } from "./Column";
import { List } from "./List";
import type { BoardFilters, ColumnData } from '../../models';
import { Icon } from '..';
import { TextInput } from 'flowbite-react';
import { KanBanDragLockContext } from './KanBanDragLockContext';

const BOARD_SCROLL_STEP = 320;
const BOARD_BOTTOM_GAP_PX = 8;
const BOARD_MIN_COLUMN_HEIGHT_PX = 240;

interface KanBanProps {
    board: Board;
    filters?: BoardFilters;
    expanded?: boolean;
    showList?: boolean;
    compactList?: boolean;
    listSort?: 'newest' | 'oldest' | 'priority';
    onAddColumn: (columnTitle: string) => void;
    onEditColumn: (column: ColumnData) => void;
    onDuplicateColumn?: (column: ColumnData) => void;
    onRemoveColumn: (column: ColumnData) => void;
    onAddCard: (initial: AnyObj, boardId: string, columnId: string, showNew: boolean) => void;
    onEditCard: (change: Change) => void;
    onDragCard: (result: AnyObj) => void;
    onLabelClick?: (labelId: string) => void;
    hideLabelNames?: boolean;
    showPinAction?: boolean;
    pinnedChangeIds?: string[];
    onTogglePin?: (change: Change) => void;
    onOpenInNewTab?: (change: Change) => void;
}

const KanBan = (props: KanBanProps) => {
    const addColumnInput = useRef<HTMLInputElement>(null);
    const boardRef = useRef<HTMLElement>(null);
    const [newColumnTitle, setNewColumnTitle] = useState("");
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [dragType, setDragType] = useState<string | null>(null);
    const [columnHeightPx, setColumnHeightPx] = useState<number | null>(null);
    const stableBoardRef = useRef(props.board);
    const dragLock = React.useMemo(
        () => ({ isDragging, dragType }),
        [isDragging, dragType]
    );

    const { t } = useTranslation();
    const {
        board, expanded, showList, compactList, listSort, filters,
        onAddColumn, onEditColumn, onDuplicateColumn, onRemoveColumn,
        onAddCard, onEditCard, onDragCard, onLabelClick, hideLabelNames,
        showPinAction, pinnedChangeIds, onTogglePin, onOpenInNewTab,
    } = props;

    // Freeze board layout while dragging so API/mercure updates cannot remount
    // droppables mid-drag (react-beautiful-dnd "Invariant failed").
    if (!isDragging)
        stableBoardRef.current = board;

    const renderBoard = isDragging ? stableBoardRef.current : board;

    const updateScrollButtons = useCallback(() => {
        const boardElement = boardRef.current;
        if (!boardElement) {
            setCanScrollLeft(false);
            setCanScrollRight(false);
            return;
        }

        setCanScrollLeft(boardElement.scrollLeft > 0);
        setCanScrollRight(
            boardElement.scrollLeft + boardElement.clientWidth < boardElement.scrollWidth - 1
        );
    }, []);

    // Cap column max-height to the visible space under the board chrome
    // (avoids fragile 100vh-rem math that clips cards). Do not force height —
    // short columns stay content-sized.
    const updateColumnHeight = useCallback(() => {
        const boardElement = boardRef.current;
        if (!boardElement)
            return;

        const top = boardElement.getBoundingClientRect().top;
        const next = Math.max(
            BOARD_MIN_COLUMN_HEIGHT_PX,
            Math.floor(window.innerHeight - top - BOARD_BOTTOM_GAP_PX)
        );
        setColumnHeightPx((prev) => (prev === next ? prev : next));
    }, []);

    useEffect(() => {
        const boardElement = boardRef.current;
        if (!boardElement)
            return;

        updateScrollButtons();
        updateColumnHeight();
        boardElement.addEventListener('scroll', updateScrollButtons);
        window.addEventListener('resize', updateColumnHeight);
        // Capture scroll from any nested/ancestor scroller (filters, page, …).
        window.addEventListener('scroll', updateColumnHeight, true);

        const resizeObserver = new ResizeObserver(() => {
            updateScrollButtons();
            updateColumnHeight();
        });
        resizeObserver.observe(boardElement);
        if (boardElement.parentElement)
            resizeObserver.observe(boardElement.parentElement);

        return () => {
            boardElement.removeEventListener('scroll', updateScrollButtons);
            window.removeEventListener('resize', updateColumnHeight);
            window.removeEventListener('scroll', updateColumnHeight, true);
            resizeObserver.disconnect();
        };
    }, [renderBoard?.updatedAt, renderBoard?.columns?.length, expanded, updateScrollButtons, updateColumnHeight]);

    const scrollBoard = (direction: 'left' | 'right') => {
        boardRef.current?.scrollBy({
            left: direction === 'left' ? -BOARD_SCROLL_STEP : BOARD_SCROLL_STEP,
            behavior: 'smooth',
        });
    };

    const addColumn = () => {
        if (newColumnTitle.length > 0)
            onAddColumn(newColumnTitle);

        setNewColumnTitle("");

        if (addColumnInput.current)
            addColumnInput.current.focus();
    }

    const handleKeyPress = (e: AnyObj, enterCallback: () => void, escapeCallback?: () => void) => {
        if (e.key === 'Enter' || e.code === 'Enter')
            enterCallback();
        if (escapeCallback && (e.key === 'Escape' || e.code === 'Escape'))
            escapeCallback();
    }

    const handleDragStart = useCallback((start: AnyObj) => {
        setIsDragging(true);
        setDragType(start?.type || null);
    }, []);

    const handleDragEnd = useCallback((result: AnyObj) => {
        // Apply optimistic reorder + unlock in one commit so rbd's drop
        // animation targets the new home (not the frozen pre-drag list).
        flushSync(() => {
            try {
                onDragCard(result);
            } finally {
                setIsDragging(false);
                setDragType(null);
            }
        });
    }, [onDragCard]);

    const columnsContent = React.useMemo(() => {
        return renderBoard.columns
            .filter(Board.isColumnVisible)
            .map((column: ColumnData, i) => (
                <Column
                    key={column._id}
                    index={i}
                    columnId={column._id}
                    filters={filters}
                    expanded={expanded}
                    onEditColumn={onEditColumn}
                    onDuplicateColumn={onDuplicateColumn}
                    onRemoveColumn={onRemoveColumn}
                    onAddCard={(initial: AnyObj, showNew: boolean) => onAddCard(initial, renderBoard._id, column._id, showNew)}
                    onEditCard={onEditCard}
                    onLabelClick={onLabelClick}
                    hideLabelNames={hideLabelNames}
                    showPinAction={showPinAction}
                    pinnedChangeIds={pinnedChangeIds}
                    onTogglePin={onTogglePin}
                    onOpenInNewTab={onOpenInNewTab}
                />
            ));
        // Do not depend on isDragging: remounting columns mid-drag breaks rbd
        // hit-testing for columns that sit before nested card droppables.
    }, [renderBoard?.updatedAt, filters?.search, filters?.user?._id, filters?.status, filters?.labelId, expanded, onLabelClick, hideLabelNames, showPinAction, pinnedChangeIds, onTogglePin, onOpenInNewTab]);

    if(!renderBoard.columns.length)
        return (
            <div className="flex flex-col justify-center items-center w-full h-full bg-gray-100 dark:bg-gray-900">
                <h2 className="heading-lg text-gray-500 dark:text-gray-400 font-light text-center">
                    {t('projects.empty_board')}<br/>
                    {t('projects.add_first_column')}
                </h2>
                <div className="flex align-start shrink-0 p-2 bg-gray-50 dark:bg-gray-900 rounded-lg">
                    <TextInput
                        rightIcon={() => <Icon type="plus" />}
                        placeholder={t('projects.add_column') as string}
                        type="text"
                        onChange={e => setNewColumnTitle(e.target.value)}
                        onKeyUp={(e) => handleKeyPress(e, addColumn, () => setNewColumnTitle(""))}
                        value={newColumnTitle}
                        ref={addColumnInput}
                    />
                </div>
            </div>
        );

    if (showList) {
        const displayBoard = renderBoard.forDisplay(filters || {} as BoardFilters);
        // List cockpit = open work only; keep finished when explicitly filtered by status.
        const closedStatuses = [
            Change.STATUS_FINISHED,
            Change.STATUS_DEPLOYED,
            Change.STATUS_MERGED,
        ];
        const listBoard = closedStatuses.includes(filters?.status || '')
            ? displayBoard
            : displayBoard.withoutFinished();

        return (
            <List
                board={listBoard}
                onEditCard={onEditCard}
                onLabelClick={onLabelClick}
                hideLabelNames={hideLabelNames}
                compact={compactList}
                sort={listSort}
                showPinAction={showPinAction}
                pinnedChangeIds={pinnedChangeIds}
                onTogglePin={onTogglePin}
                onOpenInNewTab={onOpenInNewTab}
            />
        );
    }

    const columnHeightStyle = columnHeightPx
        ? { ['--yoda-board-column-height' as any]: `${columnHeightPx}px` }
        : undefined;

    return (
        <div className="relative h-full w-full" style={columnHeightStyle}>
            {canScrollLeft && (
                <button
                    type="button"
                    aria-label={t('projects.scroll_board_left') as string}
                    className="absolute left-1 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/90 dark:bg-gray-800/90 p-2 shadow-md border border-gray-200 dark:border-gray-600 hover:bg-white dark:hover:bg-gray-700"
                    onClick={() => scrollBoard('left')}
                >
                    <Icon type="left" size={5} />
                </button>
            )}
            {canScrollRight && (
                <button
                    type="button"
                    aria-label={t('projects.scroll_board_right') as string}
                    className="absolute right-1 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/90 dark:bg-gray-800/90 p-2 shadow-md border border-gray-200 dark:border-gray-600 hover:bg-white dark:hover:bg-gray-700"
                    onClick={() => scrollBoard('right')}
                >
                    <Icon type="right" size={5} />
                </button>
            )}
            <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                <KanBanDragLockContext.Provider value={dragLock}>
                    <Droppable droppableId="board" type="column" direction="horizontal">
                        {(provided: AnyObj) => (
                            <main
                                // Droppable = scroll container so column hit-testing spans full board width.
                                className="overflow-x-auto h-full w-full scrollbar-thin scrollbar-thumb-mainPurple scrollbar-track-transparent flex items-start space-x-2"
                                {...provided.droppableProps}
                                ref={(el: HTMLElement | null) => {
                                    provided.innerRef(el);
                                    (boardRef as React.MutableRefObject<HTMLElement | null>).current = el;
                                }}
                            >
                                { columnsContent }
                                { provided.placeholder }
                                {renderBoard.acceptNewColumns && (
                                <div className="flex align-start w-[280px] h-[60px] shrink-0 p-2 border border-blue-200 dark:border-slate-700 rounded-lg bg-blue-100 dark:bg-slate-800/80">
                                    <TextInput
                                        rightIcon={() => <Icon type="plus" />}
                                        placeholder={t('projects.add_column') as string}
                                        type="text"
                                        onChange={e => setNewColumnTitle(e.target.value)}
                                        onKeyUp={(e) => handleKeyPress(e, addColumn, () => setNewColumnTitle(""))}
                                        value={newColumnTitle}
                                        className="w-full"
                                    />
                                </div>
                                )}
                            </main>
                        )}
                    </Droppable>
                </KanBanDragLockContext.Provider>
            </DragDropContext>
        </div>
    );
}

export default KanBan
